/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}


/* REI_NATIONWIDE_STOREFRONT_FIX_V1 */
(() => {
  if (window.location.pathname !== '/') return;

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const money = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  }).format(Number(value));

  async function renderReiStorefront() {
    document.documentElement.dataset.reiBrandReady = 'true';
    document.title = 'REI Nationwide | Affordable Fragrances';

    for (const node of document.querySelectorAll('header *, footer *')) {
      if (node.children.length === 0 && node.textContent.trim() === 'My Store') {
        node.textContent = 'REI Nationwide';
      }
    }

    const hero = document.querySelector('[id$="__image_banner"] h1, [id$="__image_banner"] h2');
    if (hero) hero.textContent = 'Affordable fragrance. Nationwide confidence.';

    const section = document.querySelector('[id$="__featured_collection"]');
    if (!section) return;
    const heading = section.querySelector('.collection__title h2');
    if (heading) heading.textContent = 'The REI Nationwide collection';

    const response = await fetch('/products.json?limit=10', {
      headers: { Accept: 'application/json' }, cache: 'no-store',
    });
    if (!response.ok) throw new Error(`products.json ${response.status}`);
    const { products = [] } = await response.json();
    if (!products.length) throw new Error('products.json returned no products');

    const grid = section.querySelector('ul.product-grid');
    if (!grid) return;
    grid.replaceChildren();

    for (const [index, product] of products.entries()) {
      const variant = product.variants?.[0];
      const image = product.images?.[0];
      const li = document.createElement('li');
      li.className = 'grid__item rei-grid-item';
      li.style.setProperty('--animation-order', String(index + 1));
      li.innerHTML = `
        <article class="rei-product-card card-wrapper product-card-wrapper underline-links-hover">
          <a class="rei-product-link full-unstyled-link" href="/products/${encodeURIComponent(product.handle)}">
            <div class="rei-product-media">
              ${image ? `<img src="${escapeHtml(image.src)}" alt="${escapeHtml(product.title)} fragrance by REI Nationwide" loading="${index < 4 ? 'eager' : 'lazy'}" width="${image.width || 800}" height="${image.height || 800}">` : ''}
            </div>
            <div class="rei-product-copy">
              <p class="rei-eyebrow">REI Nationwide fragrance</p>
              <h3 class="card__heading h5">${escapeHtml(product.title)}</h3>
              <p class="price"><span class="price-item price-item--regular">${money(variant?.price || 0)} USD</span></p>
            </div>
          </a>
        </article>`;
      grid.appendChild(li);
    }

    let style = document.getElementById('rei-storefront-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'rei-storefront-style';
      style.textContent = `
        .rei-product-card{height:100%;border:1px solid rgba(18,18,18,.08);border-radius:14px;overflow:hidden;background:#fff;box-shadow:0 8px 30px rgba(18,18,18,.06);transition:transform .2s ease,box-shadow .2s ease}
        .rei-product-card:hover{transform:translateY(-4px);box-shadow:0 14px 36px rgba(18,18,18,.12)}
        .rei-product-link{display:flex;flex-direction:column;height:100%;text-decoration:none}
        .rei-product-media{aspect-ratio:4/5;overflow:hidden;background:#f4f1ed}
        .rei-product-media img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .35s ease}
        .rei-product-card:hover img{transform:scale(1.035)}
        .rei-product-copy{padding:1.4rem 1.5rem 1.6rem}
        .rei-product-copy h3{margin:.35rem 0 .7rem;font-size:1.7rem}
        .rei-eyebrow{margin:0;text-transform:uppercase;letter-spacing:.12em;font-size:1rem;color:rgba(18,18,18,.58)}
        .rei-product-copy .price{margin:0;font-weight:600}
      `;
      document.head.appendChild(style);
    }

    document.body.dataset.reiProductsRendered = String(products.length);
    window.dispatchEvent(new CustomEvent('rei:storefront-ready', { detail: { count: products.length } }));
  }

  const start = () => renderReiStorefront().catch((error) => {
    document.body.dataset.reiStorefrontError = error.message;
    console.error('[REI storefront]', error);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
